import { ALL_NAV, canSee, tabHref } from '../nav/adminNavItems.js'

// Non-admin destinations — dashboard tabs and other top-level routes, each
// its own route (see router/index.js). No `group` since they're not part of
// the admin sidebar. `keywords` are extra search-only terms that don't show
// in the UI — e.g. "dashboard" is what people call the '/' route generally,
// even though its own tab label is the more specific "Attendance".
const DASHBOARD_VIEWS = [
  { to: '/',              label: 'Attendance',     permission: null, keywords: ['dashboard', 'home'] },
  { to: '/signups',       label: 'Lesson Signups', permission: ['admin', 'lesson_signups'] },
  { to: '/stats',         label: 'Stats',          permission: null },
  { to: '/students',      label: 'Students',       permission: null },
  { to: '/my-schedule',   label: 'My Schedule',    permission: null },
]

// Split out of the sidebar's single "Calendar" link (marked `searchable:
// false` in adminNavItems.js) so search offers the 3 actual views directly —
// "calendar" should return exactly these, "Week" (DayView) first since it's the
// default landing view (router/index.js redirects /admin/calendar → .../day).
// The real WeekView is disabled — see calendarTabs.js.
const CALENDAR_VIEWS = [
  { to: '/admin/calendar/day',   label: 'Week',  permission: null },
  // { to: '/admin/calendar/week',  label: 'Week',  permission: null },
  { to: '/admin/calendar/month', label: 'Month', permission: null },
]

// Flattens AdminNav.vue's grouped sidebar into a single searchable list, using
// the same canSee() gating so global search never offers a destination the
// user can't actually open. `permissions`/`tenantId` are () => value getters.
// Returns a plain function (call it to get the current list), not a computed
// — callers may pass getters over non-reactive state (e.g. AppRoot.vue reads
// straight from localStorage), where a computed's caching would go stale.
export function useSearchableViews(permissions, tenantId) {
  return () => {
    const perms = permissions()
    const tid = tenantId()

    const visibleChildren = ALL_NAV
      .filter(group => canSee(group, perms, tid))
      .flatMap(group => group.children.filter(child => canSee(child, perms, tid)).map(child => ({ group, child })))

    const adminViews = visibleChildren
      .filter(({ child }) => child.searchable !== false)
      .map(({ group, child }) => ({ to: child.to, label: child.label, group: group.label, keywords: child.keywords }))

    // A view's tabs, as `isTab` entries — GlobalSearchModal hides these on an
    // empty query and only matches them on their own label/keywords, so they
    // skip the extra click without cluttering the default list. A tab named
    // the same as its view ("Payments › Payments") is just the view itself.
    const tabViews = visibleChildren
      .filter(({ child }) => child.tabs)
      .flatMap(({ group, child }) => child.tabs
        .filter(tab => tab.label !== child.label && canSee({ ...tab, permission: tab.permission ?? null }, perms, tid))
        .map(tab => ({ to: tabHref(child, tab), label: tab.label, group: `${group.label} › ${child.label}`, keywords: tab.keywords, isTab: true })))

    const dashboardViews = DASHBOARD_VIEWS
      .filter(v => canSee(v, perms, tid))
      .map(v => ({ to: v.to, label: v.label, group: 'Dashboard', keywords: v.keywords }))

    const calendarViews = CALENDAR_VIEWS
      .filter(v => canSee(v, perms, tid))
      .map(v => ({ to: v.to, label: v.label, group: 'Calendar', keywords: v.keywords }))

    return [...dashboardViews, ...calendarViews, ...adminViews, ...tabViews]
  }
}
